import { useEffect, useRef } from 'react';
import { getJWTRefresh, isValidJWT, refreshToken } from '../core/services/helpers/axios';
import { redirectToAuth } from '../utils/redirect';
import { isWebView } from '../utils/webview';
import LCEvent from '../utils/customEvents';

// Never schedule a renew tighter than this: getJWTRefresh() clamps at 0, so a device clock that
// is off would otherwise turn the chain into a permanent renew loop. Doubles as the retry delay.
const MIN_RENEW_DELAY = 30 * 1000;

interface UseTokenRefreshOptions {
    // Only renew while we hold a session. Flipping this to false tears the chain down.
    enabled: boolean;
    // Called when a renew failed, so the caller can drop back to its loading state.
    onError?: (error: any) => void;
}

/**
 * Owns the proactive JWT renewal loop.
 *
 * The timer lives in this hook rather than in getMe, which used to start a new chain on every
 * call (mount, foreground, offline transition) and never cleared one. It is also rearmed on
 * every foreground: iOS suspends timers while the webview is backgrounded, so a renew scheduled
 * before going to background fires late or not at all, and the delay has to be re-derived from
 * whatever lifetime the current token actually has left.
 */
export const useTokenRefresh = ({ enabled, onError }: UseTokenRefreshOptions): void => {
    // Kept in a ref so an inline onError does not retrigger the effect (and reset the timer) on
    // every render of the caller.
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> = null;

        const clear = () => {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
        };

        const schedule = (delay: number) => {
            clear();
            if (cancelled) {
                return;
            }
            const safeDelay = Math.max(delay, MIN_RENEW_DELAY);
            console.log(`useTokenRefresh: next renew in ${safeDelay / 1000}s`);
            timer = setTimeout(renew, safeDelay);
        };

        const renew = async () => {
            try {
                const res = await refreshToken();
                if (cancelled) {
                    return;
                }
                // In a webview the cookie can lag behind the response, so read the lifetime from
                // the token we were just handed rather than from document.cookie.
                schedule(isWebView() && res?.data?.token ? getJWTRefresh(res.data.token) : getJWTRefresh());
            } catch (error) {
                if (cancelled) {
                    return;
                }
                console.log('useTokenRefresh: renew failed', error, 'status', error.response?.status);
                onErrorRef.current?.(error);
                if (error.response?.status === 403) {
                    redirectToAuth('Code - igPQIrcr', error);
                    return;
                }
                // Anything else (offline, 5xx) is transient: keep the chain alive instead of
                // letting it die silently until the next foreground.
                schedule(MIN_RENEW_DELAY);
            }
        };

        const handleForeground = () => {
            if (cancelled) {
                return;
            }
            if (isValidJWT()) {
                schedule(getJWTRefresh());
            } else {
                // The scheduled renew never ran while we were suspended.
                renew();
            }
        };

        schedule(getJWTRefresh());
        window.addEventListener(LCEvent.FOREGROUND, handleForeground);

        return () => {
            cancelled = true;
            clear();
            window.removeEventListener(LCEvent.FOREGROUND, handleForeground);
        };
    }, [enabled]);
};
